import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { addedInputAllowed, type AddedAssetTheme } from 'fym-shared/src/music24/addedAssets';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { AddAssetRequest, AddAssetTab, closeAddAssetDialog } from './addAssetDialogStore';
import { useOpenMusicVideoEditor } from './useOpenMusicVideoEditor';
import { YouTubeTab } from './YouTubeTab';
import { UploadTab } from './UploadTab';
import styles from './AddAssetDialog.module.scss';

const TITLES: Readonly<Record<AddedAssetTheme, MessageDescriptor>> = {
  musicVideo: messages.titleMusicVideo,
  lyricVideo: messages.titleLyricVideo,
  visualizer: messages.titleVisualizer,
  canvasLoop: messages.titleCanvasLoop,
};

const TAB_LABELS: Readonly<Record<AddAssetTab, MessageDescriptor>> = {
  youtube: messages.tabYouTube,
  upload: messages.tabUpload,
  make: messages.tabMake,
};

/**
 * The tabs of an asset type (design a2–a4, a7): "From YouTube" for the long videos (the Canvas takes
 * a file only), "Upload the file", and "Make one instead" for the music video only (the editor).
 * Pure.
 */
export function addAssetTabs(themeId: AddedAssetTheme): AddAssetTab[] {
  const tabs: AddAssetTab[] = [];
  if (addedInputAllowed(themeId, 'youtube')) {
    tabs.push('youtube');
  }
  tabs.push('upload');
  if (themeId === 'musicVideo') {
    tabs.push('make');
  }
  return tabs;
}

const AddAssetDialogBody: React.FC<{ request: AddAssetRequest }> = ({ request }) => {
  const intl = useIntl();
  const tabs = useMemo(() => addAssetTabs(request.themeId), [request.themeId]);
  const [tab, setTab] = useState<AddAssetTab>(() =>
    request.tab !== undefined && tabs.includes(request.tab) ? request.tab : (tabs[0] ?? 'upload')
  );
  const openEditor = useOpenMusicVideoEditor(request.accountId);

  const tabBar = (
    <div
      className={styles.tabs}
      role="tablist"
      aria-label={intl.formatMessage(TITLES[request.themeId], { title: request.songTitle })}
    >
      {tabs.map((one) => (
        <button
          key={one}
          type="button"
          role="tab"
          aria-selected={one === tab}
          className={clsx(styles.tab, one === tab && styles.tabOn)}
          onClick={() => setTab(one)}
          data-testid={`added-tab-${one}`}
        >
          {intl.formatMessage(TAB_LABELS[one])}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <ModalHeader toggle={closeAddAssetDialog}>
        {intl.formatMessage(TITLES[request.themeId], { title: request.songTitle })}
        <span className={clsx('d-block', styles.sub)}>
          {intl.formatMessage(request.themeId === 'canvasLoop' ? messages.subCanvas : messages.sub)}
        </span>
      </ModalHeader>
      {tab === 'youtube' && <YouTubeTab request={request} tabs={tabBar} />}
      {tab === 'upload' && <UploadTab request={request} tabs={tabBar} />}
      {tab === 'make' && (
        <>
          <ModalBody className={styles.body}>
            {tabBar}
            <p className={styles.sub}>{intl.formatMessage(messages.makeBody)}</p>
          </ModalBody>
          <ModalFooter>
            <SMLButton onClick={closeAddAssetDialog}>{intl.formatMessage(messages.cancel)}</SMLButton>
            <SMLButton
              variant="primary"
              onClick={() => {
                closeAddAssetDialog();
                openEditor(request.catalogItemUuid);
              }}
            >
              {intl.formatMessage(messages.makeOpen)}
            </SMLButton>
          </ModalFooter>
        </>
      )}
    </>
  );
};

interface AddAssetDialogProps {
  request: AddAssetRequest | null;
}

/**
 * "Add the music video of <song>" (design music-video a2–a4; spec 2026-10-09 §3.3, D2, D3): one
 * dialog for the music video, the lyric video, the visualizer and the Canvas loop. The body mounts
 * on open, so each open starts on its first tab; a running upload shows again in the upload tab.
 */
const AddAssetDialogComponent: React.FC<AddAssetDialogProps> = ({ request }) => (
  <Modal isOpen={request !== null} toggle={closeAddAssetDialog} centered size="lg" data-testid="add-asset-dialog">
    {request !== null && (
      <AddAssetDialogBody
        key={`${request.catalogItemUuid}:${request.themeId}:${request.tab ?? ''}`}
        request={request}
      />
    )}
  </Modal>
);

export const AddAssetDialog = TypedMemo(AddAssetDialogComponent);
